/**
 * Room annotation geometry properties — registration and getters.
 *
 * Properties registered here:
 * - room_polygon (FLOAT_ARRAY, PROJECT_GLOBAL) — flat [x1,y1,z1, x2,y2,z2, ...]
 * - room_base (FLOAT_ARRAY, PROJECT_GLOBAL) — [px, py, pz, nx, ny, nz]
 * - room_ceiling (FLOAT_ARRAY, PROJECT_GLOBAL) — [px, py, pz, nx, ny, nz]
 * - room_group_link (STRING, PROJECT_GLOBAL, keyed by the room ANNOTATION id) —
 *   the identifier of the room's linked ROOM group ("room folder"). Empty =
 *   never reconciled (a folder will be auto-created); ROOM_GROUP_LINK_NONE =
 *   tombstone (the user deleted the folder; never auto-recreate).
 * - room_group_position (COORDINATE, PROJECT_GLOBAL, keyed by the ROOM GROUP id) —
 *   the folder's 2D anchor (room polygon centroid; z = floor height).
 */

import { AnnotationType, PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';
import { TypedFloatArrayProperty } from '../../reactive/TypedFloatArrayProperty';

/** Tombstone value for room_group_link: folder was deleted, don't auto-recreate. */
export const ROOM_GROUP_LINK_NONE = 'NONE';

export type Coordinate = { x: number; y: number; z: number };
export type RoomPlane = { position: Coordinate; normal: Coordinate };

// --- Encode/decode helpers ---

function encodePolygon(coords: Coordinate[]): number[] {
  const arr = new Array(coords.length * 3);
  for (let i = 0; i < coords.length; i++) {
    arr[i * 3] = coords[i].x;
    arr[i * 3 + 1] = coords[i].y;
    arr[i * 3 + 2] = coords[i].z;
  }
  return arr;
}

function decodePolygon(raw: unknown): Coordinate[] {
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'number') {
    // New format: flat number array
    const coords: Coordinate[] = [];
    for (let i = 0; i + 2 < raw.length; i += 3) {
      coords.push({ x: raw[i], y: raw[i + 1], z: raw[i + 2] });
    }
    return coords;
  }
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'object') {
    return raw as Coordinate[]; // Old format: object array
  }
  if (typeof raw === 'string') {
    return decodePolygon(JSON.parse(raw)); // Legacy JSON string
  }
  return [];
}

function encodePlane(plane: RoomPlane): number[] {
  return [plane.position.x, plane.position.y, plane.position.z, plane.normal.x, plane.normal.y, plane.normal.z];
}

function decodePlane(raw: unknown): RoomPlane {
  if (Array.isArray(raw) && raw.length >= 6 && typeof raw[0] === 'number') {
    // New format: [px, py, pz, nx, ny, nz]
    return {
      position: { x: raw[0], y: raw[1], z: raw[2] },
      normal: { x: raw[3], y: raw[4], z: raw[5] },
    };
  }
  if (raw && typeof raw === 'object' && 'position' in raw) {
    return raw as RoomPlane; // Old format: object
  }
  if (typeof raw === 'string') {
    return decodePlane(JSON.parse(raw)); // Legacy JSON string
  }
  return { position: { x: 0, y: 0, z: 0 }, normal: { x: 0, y: 0, z: 1 } };
}

// --- Registration ---

export function registerRoomGeometryProperties() {
  if (!propertyRegistry.isRegistered('room_polygon')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'room_polygon',
      name: 'Polygon',
      valueType: PropertyValueType.FLOAT_ARRAY,
      defaultValue: [],
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('room_base')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'room_base',
      name: 'Base',
      valueType: PropertyValueType.FLOAT_ARRAY,
      defaultValue: [0, 0, 0, 0, 0, 1],
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('room_ceiling')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'room_ceiling',
      name: 'Ceiling',
      valueType: PropertyValueType.FLOAT_ARRAY,
      defaultValue: [0, 0, 0, 0, 0, -1],
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('room_group_link')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'room_group_link',
      name: 'Room Folder Link',
      valueType: PropertyValueType.STRING,
      defaultValue: '',
      annotationTypes: [AnnotationType.ROOM],
      ui: { hidden: true },
    });
  }

  if (!propertyRegistry.isRegistered('room_group_position')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'room_group_position',
      name: 'Room Folder Position',
      valueType: PropertyValueType.COORDINATE,
      defaultValue: { x: 0, y: 0, z: 0 },
      annotationTypes: [AnnotationType.ROOM],
      ui: { hidden: true },
    });
  }
}

registerRoomGeometryProperties();

// --- Getters ---

const roomPolygonCache = new Map<string, TypedFloatArrayProperty<Coordinate[]>>();
const roomBaseCache = new Map<string, TypedFloatArrayProperty<RoomPlane>>();
const roomCeilingCache = new Map<string, TypedFloatArrayProperty<RoomPlane>>();

export function getRoomPolygonProperty(annotationId: string): TypedFloatArrayProperty<Coordinate[]> {
  const cached = roomPolygonCache.get(annotationId);
  if (cached && !cached.isDestroyed) return cached;

  const service = getGlobalPropertyService();
  const raw = service.getProperty('room_polygon', annotationId, PropertyType.PROJECT_GLOBAL);
  const typed = new TypedFloatArrayProperty<Coordinate[]>(raw, encodePolygon, decodePolygon);
  roomPolygonCache.set(annotationId, typed);
  return typed;
}

export function getRoomBaseProperty(annotationId: string): TypedFloatArrayProperty<RoomPlane> {
  const cached = roomBaseCache.get(annotationId);
  if (cached && !cached.isDestroyed) return cached;

  const service = getGlobalPropertyService();
  const raw = service.getProperty('room_base', annotationId, PropertyType.PROJECT_GLOBAL);
  const typed = new TypedFloatArrayProperty<RoomPlane>(raw, encodePlane, decodePlane);
  roomBaseCache.set(annotationId, typed);
  return typed;
}

export function getRoomCeilingProperty(annotationId: string): TypedFloatArrayProperty<RoomPlane> {
  const cached = roomCeilingCache.get(annotationId);
  if (cached && !cached.isDestroyed) return cached;

  const service = getGlobalPropertyService();
  const raw = service.getProperty('room_ceiling', annotationId, PropertyType.PROJECT_GLOBAL);
  const typed = new TypedFloatArrayProperty<RoomPlane>(raw, encodePlane, decodePlane);
  roomCeilingCache.set(annotationId, typed);
  return typed;
}

/**
 * The room's link to its ROOM group ("room folder"), keyed by the room
 * ANNOTATION identifier. '' = never reconciled; ROOM_GROUP_LINK_NONE =
 * user-deleted tombstone; anything else = the linked group's identifier.
 */
export function getRoomGroupLinkProperty(roomAnnotationId: string): ReactiveProperty<string> {
  const service = getGlobalPropertyService();
  return service.getProperty('room_group_link', roomAnnotationId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<string>;
}

/** The room folder's 2D anchor (polygon centroid), keyed by the ROOM GROUP identifier. */
export function getRoomGroupPositionProperty(roomGroupId: string): ReactiveProperty<Coordinate> {
  const service = getGlobalPropertyService();
  return service.getProperty('room_group_position', roomGroupId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
}
